import {
  AfterViewInit,
  Component,
  EffectRef,
  ElementRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  untracked,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DialogService } from '@design-system/lib/dialog/dialog.service';
import { DialogActionTypeEnum } from '@design-system/lib/dialog/models/dialog.model';
import { Observable, Subscription } from 'rxjs';
import { ModalService } from '@app/core/services/modal.service';
import { ModalConfig } from '@app/core/services/modal.service';

interface DialogRef {
  dismiss: () => void;
  close?: (result?: unknown) => void;
  closed$?: Observable<unknown>;
}

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() renderAsDialogContent = false;
  @Input() config: ModalConfig | null = null;
  @Input() dialogRef?: DialogRef;
  @Input() onConfirmRequest?: (value: string) => void;
  @Input() onCancelRequest?: () => void;
  @Input() dataChange?: (data: string) => void;
  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  private readonly injector = inject(Injector);
  private readonly dialogService = inject(DialogService);
  readonly modalService = inject(ModalService);

  inputValue = '';
  private hostDialogRef?: DialogRef;
  private openWatcher?: EffectRef;
  private hostDialogClosedSub?: Subscription;

  ngOnInit(): void {
    if (this.renderAsDialogContent) {
      this.inputValue = this.config?.inputValue ?? '';
      this.dataChange?.(this.inputValue);
      return;
    }

    this.openWatcher = effect(() => {
      const isOpen = this.modalService.isOpen();
      const currentConfig = this.modalService.config();

      if (isOpen && currentConfig && !this.hostDialogRef) {
        untracked(() => this.openDialog(currentConfig));
      }

      if (!isOpen && this.hostDialogRef) {
        untracked(() => {
          this.hostDialogRef?.dismiss();
          this.hostDialogRef = undefined;
        });
      }
    }, { injector: this.injector });
  }

  ngAfterViewInit(): void {
    if (this.renderAsDialogContent && this.config?.type === 'prompt') {
      setTimeout(() => {
        this.inputEl?.nativeElement?.focus();
        this.inputEl?.nativeElement?.select();
      }, 50);
    }
  }

  ngOnDestroy(): void {
    this.openWatcher?.destroy();
    this.hostDialogClosedSub?.unsubscribe();
    if (!this.renderAsDialogContent) {
      this.hostDialogRef?.dismiss();
      this.hostDialogRef = undefined;
    }
  }

  get activeConfig(): ModalConfig | null {
    return this.renderAsDialogContent ? this.config : this.modalService.config();
  }

  private openDialog(config: ModalConfig): void {
    const ref = this.dialogService.show({
      title: config.title,
      component: ModalComponent,
      componentInputs: {
        renderAsDialogContent: true,
        config,
        onConfirmRequest: (value: string) => this.finalizeConfirm(value),
        onCancelRequest: () => this.finalizeCancel(),
      },
      withDataBindings: true,
      actions: [
        ...(config.type !== 'alert' ? [{
          type: DialogActionTypeEnum.DISMISS,
          label: config.cancelText || 'Cancel',
          callback: (_ref: { close: () => void }) => {
            this.finalizeCancel();
          },
        }] : []),
        {
          type: DialogActionTypeEnum.PRIMARY,
          label: config.confirmText || 'OK',
          callback: (_ref: { close: () => void }, extraData?: unknown) => {
            const value = typeof extraData === 'string' ? extraData : this.inputValue;
            this.finalizeConfirm(value);
          },
        },
      ],
      withDialogRef: true,
      canCloseWithBackdrop: false,
      width: '480px',
      maxWidth: '95vw',
      bodyStyles: { padding: '12px 20px' },
      footerStyles: { padding: '12px 20px', gap: '8px' },
    }) as DialogRef;

    this.hostDialogRef = ref;
    this.hostDialogClosedSub?.unsubscribe();
    this.hostDialogClosedSub = ref.closed$?.subscribe(() => {
      // When closed from the DS header close button, resolve the pending prompt as cancel.
      if (this.modalService.isOpen()) {
        this.finalizeCancel();
      }
      this.hostDialogRef = undefined;
    });
  }

  onInputValueChange(value: string): void {
    this.inputValue = value;
    if (this.renderAsDialogContent) {
      this.dataChange?.(value);
    }
  }

  onConfirm(): void {
    if (this.renderAsDialogContent) {
      this.onConfirmRequest?.(this.inputValue);
      this.dialogRef?.dismiss();
      return;
    }

    this.finalizeConfirm(this.inputValue);
  }

  private finalizeConfirm(value: string): void {
    this.modalService.close({
      confirmed: true,
      value,
    });
    this.inputValue = '';
  }

  onCancel(): void {
    if (this.renderAsDialogContent) {
      this.onCancelRequest?.();
      this.dialogRef?.dismiss();
      return;
    }

    this.finalizeCancel();
  }

  private finalizeCancel(): void {
    this.modalService.close({
      confirmed: false,
    });
    this.inputValue = '';
  }
}
